import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Activity, Cloud, Server, ShieldCheck, Zap } from "lucide-react";
import {
  SiAmazonaws,
  SiFirebase,
  SiCloudflare,
  SiVercel,
  SiDigitalocean,
} from "react-icons/si";
import { cn } from "../../lib/utils";

const STATS_ICONS = [Activity, ShieldCheck, Zap];

const PROVIDER_ROWS = [
  { key: "aws", icon: SiAmazonaws },
  { key: "cloudflare", icon: SiCloudflare },
  { key: "vercel", icon: SiVercel },
  { key: "firebase", icon: SiFirebase },
  { key: "digitalocean", icon: SiDigitalocean },
  { key: "selfHosted", icon: Server },
];

export default function UptimeSection({ className }) {
  const { t, i18n } = useTranslation();

  const providerDisplayName = useMemo(
    () => ({
      aws: "AWS",
      cloudflare: "Cloudflare",
      vercel: "Vercel",
      firebase: "Firebase",
      digitalocean: "DigitalOcean",
      selfHosted: t("home.uptime.selfHostedLabel"),
    }),
    [t, i18n.language],
  );

  const stats = useMemo(() => {
    const rows = Array.isArray(
      t("home.uptime.stats", { returnObjects: true }),
    )
      ? t("home.uptime.stats", { returnObjects: true })
      : [];
    return rows.map((row, i) => ({
      ...row,
      icon: STATS_ICONS[i] ?? Activity,
    }));
  }, [t, i18n.language]);
  return (
    <section
      className={cn(
        "relative w-full overflow-hidden bg-black text-white py-24 md:py-32",
        className,
      )}
    >
      <div className="pointer-events-none absolute inset-0 opacity-70 [background:radial-gradient(60%_60%_at_50%_0%,rgba(122,149,181,0.15),transparent_60%),radial-gradient(50%_60%_at_50%_100%,rgba(122,92,255,0.12),transparent_60%)]" />

      <div className="pointer-events-none absolute inset-0 opacity-[0.06] [background-image:linear-gradient(to_right,rgba(255,255,255,0.6)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.6)_1px,transparent_1px)] [background-size:60px_60px] [mask-image:radial-gradient(ellipse_60%_60%_at_50%_50%,#000,transparent_75%)]" />

      <div className="relative mx-auto max-w-6xl px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex justify-center"
        >
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-white/70 backdrop-blur">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
            </span>
            {t("home.uptime.badge")}
          </div>
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.05 }}
          className="mt-6 text-center text-3xl md:text-5xl font-bold tracking-tight bg-gradient-to-br from-white via-white to-white/60 bg-clip-text text-transparent"
        >
          {t("home.uptime.title")}
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="mt-4 mx-auto max-w-2xl text-center text-base md:text-lg text-white/60"
        >
          {t("home.uptime.subtitle")}
        </motion.p>

        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {stats.map((stat, i) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={`${stat.label}-${i}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 + i * 0.1 }}
                className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur transition-all duration-300 hover:-translate-y-1 hover:border-white/20 hover:bg-white/[0.06]"
              >
                <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100 [background:radial-gradient(60%_60%_at_50%_0%,rgba(122,149,181,0.18),transparent_70%)]" />
                <div className="relative flex items-start gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04] text-white">
                    <Icon className="h-5 w-5" aria-hidden />
                  </div>
                  <div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-3xl font-bold tracking-tight bg-gradient-to-br from-white to-white/70 bg-clip-text text-transparent">
                        {stat.value}
                      </span>
                      <span className="text-xs uppercase tracking-widest text-white/68">
                        {stat.label}
                      </span>
                    </div>
                    <p className="mt-2 text-sm text-white/76 leading-relaxed">
                      {stat.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="mt-12 rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.04] to-transparent p-6 backdrop-blur"
        >
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04]">
                <Cloud className="h-5 w-5 text-white" aria-hidden />
              </div>
              <div>
                <div className="text-sm font-semibold text-white">
                  {t("home.uptime.multiCloud.title")}
                </div>
                <div className="text-xs text-white/68">
                  {t("home.uptime.multiCloud.subtitle")}
                </div>
              </div>
            </div>
            <div className="text-xs uppercase tracking-[0.25em] text-white/45">
              {t("home.uptime.hostedOn")}
            </div>
          </div>

          <div className="mt-6 grid grid-cols-3 gap-3 sm:grid-cols-6">
            {PROVIDER_ROWS.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.div
                  key={p.key}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.05 * i }}
                  className="flex flex-col items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[0.02] py-4 px-2 text-white/70 transition-all duration-300 hover:border-white/25 hover:bg-white/[0.05] hover:text-white"
                >
                  <Icon className="h-6 w-6" aria-hidden />
                  <span className="text-[11px] font-medium tracking-wide">
                    {providerDisplayName[p.key]}
                  </span>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
